import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import WorkerLayout from '../../layouts/WorkerLayout';
import Button from '../../components/common/Button';

const STATUS_STYLE = {
  applied:     'bg-blue-50 text-blue-700',
  shortlisted: 'bg-yellow-50 text-yellow-700',
  selected:    'bg-green-50 text-green-700',
  rejected:    'bg-red-50 text-red-700',
  withdrawn:   'bg-gray-100 text-gray-600',
};

export default function WorkerApplications() {
  const [apps, setApps] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setLoading(true);
    api.get(`/worker/applications?page=${page}&limit=10`)
      .then((r) => { setApps(r.data.data || []); setPagination(r.data.pagination || {}); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page]);

  return (
    <WorkerLayout>
      <div className="space-y-5 pb-20 sm:pb-6">
        <h1 className="text-xl font-bold text-gray-900">My Applications</h1>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-8 w-8 border-4 border-brand-500 border-t-transparent rounded-full" />
          </div>
        ) : apps.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
            <span className="text-4xl">📋</span>
            <p className="text-gray-500 mt-3">No applications yet</p>
            <p className="text-sm text-gray-400 mt-1">Browse jobs and apply to get started</p>
            <Link to="/worker/jobs"><Button size="sm" className="mt-4">Browse Jobs</Button></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {apps.map((app) => (
              <div key={app.id} className="bg-white rounded-xl border border-gray-200 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">{app.title}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{app.company_name}</p>
                    {app.wage_min && (
                      <p className="text-xs text-gray-500 mt-1">
                        ₹{app.wage_min}{app.wage_max ? `–${app.wage_max}` : ''}/day
                      </p>
                    )}
                    {app.start_date && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        Start: {new Date(app.start_date).toLocaleDateString('en-IN')}
                      </p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_STYLE[app.status] || STATUS_STYLE.applied}`}>
                      {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                    </span>
                    <p className="text-xs text-gray-400 mt-1.5">
                      {new Date(app.applied_at).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                </div>
                {app.employer_note && (
                  <div className="mt-3 bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 font-medium mb-0.5">Employer note</p>
                    <p className="text-xs text-gray-700">{app.employer_note}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {pagination.totalPages > 1 && (
          <div className="flex justify-center gap-3">
            {pagination.hasPrev && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p - 1)}>← Prev</Button>
            )}
            <span className="text-sm text-gray-500 py-1.5">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            {pagination.hasNext && (
              <Button size="sm" variant="secondary" onClick={() => setPage((p) => p + 1)}>Next →</Button>
            )}
          </div>
        )}
      </div>
    </WorkerLayout>
  );
}
